import React, { Fragment, useEffect, useState } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import { Button, FormGroup, Input } from 'reactstrap';
import { useArticlesUIContext } from '../ArticlesUIProvider';
import ArticlesTableColumns, { ArticlesColumns } from './ArticlesTableColumns';
import { ArticlesTableSearchBar } from './ArticlesTableSearchBar';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getArticleList, setArticleFilters, toggleCheckedItemById } from '../../redux/actions';
import EyeIcon from 'mdi-react/EyeIcon';
import PencilIcon from 'mdi-react/PencilIcon';
import LoadingIcon from 'mdi-react/LoadingIcon';
import { defaultPaginationProps } from '@/shared/helpers';
import DeleteIcon from 'mdi-react/DeleteIcon';

const ArticlesTable = () => {
  const articlesUIContext = useArticlesUIContext();
  const dispatch = useDispatch();
  const {
    data: articles,
    error,
    filters,
    loading,
    totalRecords,
    checkedArticleIds,
  } = useSelector((state) => state.cadsAIArticles, shallowEqual);
  const {
    pageSize: rowsPerPage,
    pageNumber: page,
    sortBy: orderBy,
    isSortOrderDescending,
  } = filters;

  const [columns, setColumns] = useState(0);

  useEffect(() => {
    setColumns(ArticlesColumns.length);
  }, [ArticlesColumns.length]);

  React.useEffect(() => {
    dispatch(getArticleList(filters));
  }, [filters]);

  const handleRequestSort = (event, property) => {
    dispatch(
      setArticleFilters({
        sortBy: property,
        isSortOrderDescending: !isSortOrderDescending,
      }),
    );
  };

  const renderActionButtons = (articleId) => (
    <div className="actions">
      <Button
        className="icon mb-0"
        color="success"
        outline
        onClick={() => articlesUIContext.openViewArticlesDialog(articleId)}
      >
        <p>
          <EyeIcon /> View
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="primary"
        outline
        onClick={() => articlesUIContext.openEditArticlesDialog(articleId)}
      >
        <p>
          <PencilIcon /> Edit
        </p>
      </Button>
      <Button
        className="icon mb-0"
        color="danger"
        outline
        onClick={() => articlesUIContext.openDeactivateArticlesDialog(articleId)}
      >
        <p>
          <DeleteIcon /> Deactivate
        </p>
      </Button>
    </div>
  );

  const handleChangePage = (event, currentPage) => {
    dispatch(setArticleFilters({ pageSize: rowsPerPage, pageNumber: currentPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setArticleFilters({ pageSize: +event.target.value, pageNumber: 1 }));
  };

  const emptyRows =
    page === Math.ceil(totalRecords / rowsPerPage) ? rowsPerPage - articles.length : 0;

  return (
    <Fragment>
      <div className="material-table__wrap">
        <ArticlesTableSearchBar />
        <Table className="material-table">
          <ArticlesTableColumns
            order={isSortOrderDescending ? 'desc' : 'asc'}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rowsPerPage}
          />
          <TableBody>
            {(loading || !!error) && (
              <TableRow>
                <TableCell colSpan={columns}>
                  {loading && (
                    <div className={'text-center overflow-hidden'}>
                      <div className="panel__refresh position-relative" style={{ height: 75 }}>
                        <LoadingIcon />
                      </div>
                    </div>
                  )}
                  <div className={'text-center'}>
                    <p className={!!error ? 'm-5' : ''}>{error || 'Loading articles...'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {!!!articles.length && !loading && !error && (
              <TableRow style={{ height: 120 }}>
                <TableCell colSpan={columns} align="center">
                  {filters.status === null && filters.search === ''
                    ? 'No Articles'
                    : 'No Articles with applied filters'}
                </TableCell>
              </TableRow>
            )}
            {articles.map((article) => {
              return (
                <TableRow className="material-table" tabIndex={-1} key={article?.id}>
                  <TableCell align="center" className="material-table__cell">
                    <FormGroup>
                      <Input
                        type="checkbox"
                        value={article?.id}
                        defaultChecked={checkedArticleIds.includes(article?.id)}
                        onChange={(e) => dispatch(toggleCheckedItemById(+e.target.value))}
                      />
                    </FormGroup>
                  </TableCell>
                  <TableCell className="material-table__cell" align="center">
                    <img
                      className={'module_logo_image'}
                      src={
                        article?.imageUrl?.startsWith('https')
                          ? article?.imageUrl
                          : 'https://unified.dev.cads.ai/image/sample/rectangle-34.png'
                      }
                      alt={'Article Image'}
                    />
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {article.title}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {article.readCount}
                  </TableCell>
                  <TableCell align="center" className="material-table__cell">
                    {article.followerCount}
                  </TableCell>
                  <TableCell
                    align="center"
                    className="material-table__cell"
                    style={{ minWidth: 250 }}
                  >
                    {renderActionButtons(article?.id)}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow style={{ height: 49 * emptyRows }}>
                <TableCell colSpan={columns} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <TablePagination
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page - 1}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        component={defaultPaginationProps.component}
        backIconButtonProps={defaultPaginationProps.backIconButton}
        nextIconButtonProps={defaultPaginationProps.nextIconButton}
        rowsPerPageOptions={defaultPaginationProps.rowsPerPage}
        className={defaultPaginationProps.className}
        dir={defaultPaginationProps.dir}
      />
    </Fragment>
  );
};

export default ArticlesTable;
